<template>
  
  <!-- Login Form: Shown when no user is logged in -->
  <div v-if="!user" class="login-container">
    <el-card class="login-card">

      <!-- Normal login -->
      <template v-if="!showForgotPw">
        <h2>Admin Login</h2>
        <el-form @submit.prevent="login" label-position="top">
          <el-form-item label="Email">
            <el-input
              v-model="email"
              type="email"
              placeholder="Email"
              clearable
            />
          </el-form-item>
          <el-form-item label="Password">
            <el-input
              v-model="password"
              type="password"
              placeholder="Password"
              show-password
            />
          </el-form-item>
          <el-form-item>
            <button type="primary" native-type="submit" :loading="loading">
              Login
            </button>
          </el-form-item>
        </el-form>
        <el-alert
          v-if="errorMessage"
          :title="errorMessage"
          type="error"
          show-icon
        />
        <div class="forgot-pw-link">
          <button type="button" class="link-btn" @click="showForgotPw = true">
            Forgot password?
          </button>
        </div>
      </template>

      <!-- Forgot password -->
      <template v-else>
        <h2>Reset password</h2>
        <p class="reset-hint">Enter your admin email and we'll send a reset link.</p>
        <el-form @submit.prevent="sendResetEmail" label-position="top">
          <el-form-item label="Admin email">
            <el-input
              v-model="resetEmail"
              type="email"
              placeholder="Email"
              clearable
            />
          </el-form-item>
          <el-form-item>
            <button type="submit" :disabled="resetLoading">
              {{ resetLoading ? 'Sending…' : 'Send reset link' }}
            </button>
          </el-form-item>
        </el-form>
        <el-alert
          v-if="resetMessage"
          :title="resetMessage"
          :type="resetIsError ? 'error' : 'success'"
          show-icon
          :closable="false"
        />
        <div class="forgot-pw-link">
          <button type="button" class="link-btn" @click="cancelForgotPw">
            &larr; Back to login
          </button>
        </div>
      </template>

    </el-card>
  </div>

  <!-- Card Management UI: Shown when user is logged in -->
  <div v-else id="card-management" class="card-management-page">
    <AdminTabs />
    <div class="admin-page-header">
      <h1>Manage Cards</h1>
      <button type="button" class="admin-btn admin-btn--primary" @click="openAddModal">
        <i class="fas fa-plus"></i> Add New Card
      </button>
    </div>
    <!-- Button to trigger manual sync -->
    <button class="yellow hidden" @click="syncCards">Sync cards</button>

    <!-- Modal for Adding or Editing Cards -->
    <SettingsModal :show="showModal" @update:show="showModal = $event">
      <template #header>
        <h3>{{ isEditing ? "Edit Card" : "Add New Card" }}</h3>
      </template>

      <template #body>
        <form @submit.prevent="isEditing ? updateCard() : addCard()">
          <div class="form-group">
            <label for="title">Title</label>
            <input
              type="text"
              id="title"
              v-model="editingCard.title"
              class="w-full"
              required
            />
          </div>

          <div class="form-row form-row--options">
            <div class="form-group department-field">
              <label>Department</label>
              <div class="department-radio-group">
                <label
                  v-for="department in departments"
                  :key="department.id"
                  class="department-radio-option"
                  :style="editingCard.department === department.id
                    ? { backgroundColor: department.primary_color, borderColor: department.primary_color }
                    : { borderColor: department.primary_color }"
                >
                  <input
                    type="radio"
                    v-model="editingCard.department"
                    :value="department.id"
                    required
                  />
                  <span :style="editingCard.department === department.id ? { color: '#fff' } : { color: department.primary_color }">{{ department.name }}</span>
                </label>
              </div>
            </div>

            <div class="form-group tier-field">
              <label>Tier</label>
              <div class="tier-radio-group">
                <label class="tier-option ranking-1">
                  <input type="radio" v-model.number="editingCard.ranking" :value="1" required />
                  <span>1</span>
                </label>
                <label class="tier-option ranking-2">
                  <input type="radio" v-model.number="editingCard.ranking" :value="2" />
                  <span>2</span>
                </label>
                <label class="tier-option ranking-3">
                  <input type="radio" v-model.number="editingCard.ranking" :value="3" />
                  <span>3</span>
                </label>
                <label class="tier-option ranking-4">
                  <input type="radio" v-model.number="editingCard.ranking" :value="4" />
                  <span>4</span>
                </label>
              </div>
            </div>

 
          </div>
          <div class="form-group">
            <label for="content">Content</label>
            <QuillEditor
              v-model:content="editingCard.content"
              content-type="html"
              :options="contentEditorOptions"
              class="quill-editor quill-editor--content"
            />
          </div>

          <div class="form-group">
            <div class="explanation-editor-header">
              <label for="explanation">Explanation</label>
              <button
                type="button"
                class="formatting-toggle"
                @click="isCardExplanationHtmlMode = !isCardExplanationHtmlMode"
              >
                <i :class="isCardExplanationHtmlMode ? 'fas fa-pen' : 'fas fa-code'"></i>
                {{ isCardExplanationHtmlMode ? 'WYSIWYG' : 'HTML' }}
              </button>
            </div>
            <textarea
              v-if="isCardExplanationHtmlMode"
              v-model="editingCard.explanation"
              class="html-source-editor"
              spellcheck="false"
            />
            <QuillEditor
              v-else
              v-model:content="editingCard.explanation"
              content-type="html"
              :options="richExplanationEditorOptions"
              class="quill-editor quill-editor--explanation"
            />
          </div>
        </form>
      </template>

      <template #footer>
        <button v-if="isEditing" type="button" class="modal-delete-link" @click="confirmDeleteCard">
          <i class="fas fa-trash"></i> Delete card
        </button>
        <label class="checkbox-label footer-demo-checkbox">
          <input type="checkbox" v-model="editingCard.useForDemo" />
          Use for demo
        </label>
        <button type="button" class="admin-btn admin-btn--primary" @click="isEditing ? updateCard() : addCard()">
          <i class="fas fa-save"></i> Save
        </button>
      </template>
    </SettingsModal>

    <!-- Explanation Modal -->
    <SettingsModal :show="showExplanationModal" @update:show="val => { if (!val) closeExplanationModal(); else showExplanationModal = val }">
      <template #header>
        <h3>{{ isEditingExplanation ? 'Edit' : 'View' }} Explanation - {{ selectedCard?.title }}</h3>
      </template>

      <template #body>
        <div v-if="!isEditingExplanation" class="explanation-content" v-html="renderExplanationHtml(selectedCard?.explanation || '')"></div>
        
        <div v-else class="explanation-edit">
          <div class="explanation-editor-header">
            <label for="explanation-edit">Edit Explanation</label>
            <button
              type="button"
              class="formatting-toggle"
              @click="isModalExplanationHtmlMode = !isModalExplanationHtmlMode"
            >
              <i :class="isModalExplanationHtmlMode ? 'fas fa-pen' : 'fas fa-code'"></i>
              {{ isModalExplanationHtmlMode ? 'WYSIWYG' : 'HTML' }}
            </button>
          </div>
          <textarea
            v-if="isModalExplanationHtmlMode"
            v-model="editingExplanation"
            class="html-source-editor html-source-editor--modal"
            spellcheck="false"
          />
          <QuillEditor
            v-else
            :key="`explanation-editor-${selectedCard?.id}-${isEditingExplanation}`"
            v-model:content="editingExplanation"
            content-type="html"
            :options="richExplanationEditorOptions"
            class="quill-editor quill-editor--modal"
          />
        </div>
      </template>

      <template #footer>
        <button v-if="!isEditingExplanation" type="button" class="admin-btn admin-btn--secondary" @click="startEditExplanation">
          <i class="fas fa-edit"></i> Edit
        </button>
        <button v-if="isEditingExplanation" type="button" class="admin-btn admin-btn--secondary" @click="isEditingExplanation = false">
          <i class="fas fa-eye"></i> View
        </button>
        <button v-if="isEditingExplanation" type="button" class="admin-btn admin-btn--primary" @click="saveExplanation" :disabled="!hasExplanationChanged">
          <i class="fas fa-save"></i> Save
        </button>
      </template>
    </SettingsModal>

      <!-- Filtering Controls -->
    <div class="card-management-top">
      <div id="filters">
        <!-- Left Section: View Controls -->
        <div class="filters-left">
          <div id="view-toggle">
            <el-form-item id="view-toggle-item">
              <button 
                @click="isCompactView = true" 
                :class="isCompactView ? 'blue' : ''"
              >
                <i class="fas fa-compress"></i>
                Compact
              </button>
              <button 
                @click="isCompactView = false" 
                :class="!isCompactView ? 'blue' : ''"
              >
                <i class="fas fa-expand"></i>
                Expanded
              </button>
            </el-form-item>
            <el-form-item id="bulk-select-item">
              <button 
                @click="toggleBulkSelectMode" 
                :class="bulkSelectMode ? 'blue' : ''"
              >
                <i :class="bulkSelectMode ? 'fas fa-times' : 'fas fa-check-square'"></i>
                {{ bulkSelectMode ? 'Unselect' : 'Bulk Select' }}
              </button>
            </el-form-item>
          </div>
        </div>

        <!-- Center Section: Bulk Actions -->
        <div class="filters-center">
          <div v-if="bulkSelectMode && selectedCardIds.size > 0" class="bulk-actions-bar">
            <span class="selected-count">{{ selectedCardIds.size }} card(s) selected</span>
            <button @click="bulkDeleteCards" class="bulk-delete-btn">
              <i class="fas fa-trash"></i>
              Delete Selected
            </button>
          </div>
        </div>

        <!-- Right Section: Filters -->
        <div class="filters-right">
          <el-form inline>
            <el-form-item id="search-filter-item">
              <el-input
                v-model="searchQuery"
                placeholder="Search cards..."
                clearable
                style="width: 200px;"
              />
            </el-form-item>
            <el-form-item id="ranking-filter-item">
              <el-select v-model="filterRanking" placeholder="All Tiers" clearable>
                <el-option label="All Tiers" value=""></el-option>
                <el-option label="1" value="1"></el-option>
                <el-option label="2" value="2"></el-option>
                <el-option label="3" value="3"></el-option>
                <el-option label="4" value="4"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item id="department-filter-item">
              <el-select v-model="filterDepartment" placeholder="All Departments" clearable>
                <el-option label="All Departments" value=""></el-option>
                <!-- Dynamically generate department options -->
                <el-option
                  v-for="department in departments"
                  :key="department.id"
                  :label="department.name"
                  :value="department.id"
                />
              </el-select>
            </el-form-item>
          </el-form>
        </div>
      </div>
    </div>

    <div class="card-table-scroll-shell">
      <div class="card-table-scroll">
        <!-- Card List -->
        <table class="card-table" :class="{ 'compact-table': isCompactView }">
          <thead>
            <tr>
              <th v-if="bulkSelectMode" class="card-select">
                <input 
                  type="checkbox" 
                  :checked="selectAll" 
                  @change="toggleSelectAll"
                  class="select-all-checkbox"
                />
              </th>
              <th class="card-number">#</th>
              <th class="card-title">Title</th>
              <th class="card-content">Content</th>
              <th class="card-explanation">Explanation</th>
              <th class="card-ranking" @click="setSort('ranking')">
                Tier <span v-if="sortKey === 'ranking'">
                  {{ sortOrder === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th class="card-department" @click="setSort('department')">
                Department <span v-if="sortKey === 'department'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
              </th>
              <th class="card-demo">Demo</th>
              <th class="card-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="card in sortedFilteredCards" 
              :key="card.id" 
              :class="{ 
                'selected-row': selectedCardIds.has(card.id),
                'clickable-row': !bulkSelectMode
              }"
              @click="bulkSelectMode ? null : editCard(card)"
            >
              <td v-if="bulkSelectMode" class="card-select">
                <input 
                  type="checkbox" 
                  :checked="selectedCardIds.has(card.id)" 
                  @change="toggleCardSelection(card.id, $event)"
                  class="row-checkbox"
                />
              </td>
              <td class="card-number">{{ getOriginalCardNumber(card.id) }}</td>
              <td class="card-title">{{ card.title }}</td>
              <td class="card-content" v-html="sanitizeHTML(card.content)"></td>
              <td class="card-explanation" :class="{ 'clickable-explanation': !isCompactView }">
                <div v-if="!isCompactView" class="explanation-preview" @click.stop="openExplanationModal(card)">
                  <div v-if="card.explanation" v-html="renderExplanationHtml(truncateExplanation(card.explanation))"></div>
                  <div v-else class="empty-explanation">
                    <i class="fas fa-plus"></i>
                    <span>Add Explanation</span>
                  </div>
                </div>
                <div v-else class="compact-explanation">
                  <button 
                    @click.stop="openExplanationModal(card)" 
                    class="action-btn blue"
                    :title="card.explanation ? 'View Explanation' : 'Add Explanation'"
                  >
                    <i :class="card.explanation ? 'fas fa-external-link-alt' : 'fas fa-plus'"></i>
                    <span>{{ card.explanation ? 'View' : 'Add' }}</span>
                  </button>
                </div>
              </td>
              <td class="card-ranking">
                <span :class="`ranking-${card.ranking}`">{{ card.ranking }}</span>
              </td>
              <td class="card-department">
                <span :class="`department-${card.department}`">{{ card.departmentName || getDepartmentName(card.department) }}</span>
              </td>
              <td class="card-demo" @click.stop>
                <input
                  type="checkbox"
                  class="demo-table-checkbox"
                  :checked="!!card.useForDemo"
                  :disabled="savingDemoIds.includes(card.id)"
                  :title="card.useForDemo ? 'Included in demo deck' : 'Not in demo deck'"
                  :aria-label="`Use ${card.title} for demo`"
                  @change="toggleCardUseForDemo(card, $event)"
                />
              </td>
              <td class="card-actions" @click.stop>
                <div class="admin-icon-btn-group">
                  <el-tooltip content="Edit card" placement="top" transition="tooltip-no-fade" :show-after="0" :hide-after="0" :enterable="false">
                    <button type="button" class="admin-icon-btn" @click="editCard(card)">
                      <i class="fas fa-pencil-alt"></i>
                    </button>
                  </el-tooltip>
                  <el-tooltip content="Delete card" placement="top" transition="tooltip-no-fade" :show-after="0" :hide-after="0" :enterable="false">
                    <button type="button" class="admin-icon-btn admin-icon-btn--delete" @click="confirmDeleteSingleCard(card)">
                      <i class="fas fa-trash"></i>
                    </button>
                  </el-tooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Add Card Button at Bottom of Table -->
      <div class="admin-table-footer">
        <button type="button" class="admin-add-row-btn" @click="openAddModal">
          <i class="fas fa-plus"></i>
          Add New Card
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import axios from "axios";
import { useGameStore } from "@/stores/gameStore";
import SettingsModal from "@/components/SettingsModal.vue";
import AdminTabs from "@/components/AdminTabs.vue";
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";




import DOMPurify from "dompurify";
import linkifyHtml from "linkify-html";
import { supabase } from "@/supabase";
import { ElMessage } from "element-plus";

const gameStore = useGameStore();
const showModal = ref(false);
const showExplanationModal = ref(false);
const selectedCard = ref(null);
const isEditing = ref(false);
const isEditingExplanation = ref(false);
const editingExplanation = ref("");
const originalExplanation = ref("");
const isCardExplanationHtmlMode = ref(false);
const isModalExplanationHtmlMode = ref(false);


// Computed property to check if explanation content has changed
const hasExplanationChanged = computed(() => {
  return editingExplanation.value !== originalExplanation.value;
});


const editingCard = ref({
  title: "",
  content: "",
  explanation: "",
  ranking: 1,
  useForDemo: false,
});

// Remember last used department and ranking for new cards
const lastUsedDepartment = ref("");
const lastUsedRanking = ref(1);

const contentEditorOptions = {
  theme: "snow",
  placeholder: "Enter card content...",
  modules: {
    toolbar: false,
  },
};

const baseExplanationToolbar = [
  [{ header: [2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "blockquote", "code-block"],
  ["clean"],
];

const richExplanationEditorOptions = {
  theme: "snow",
  placeholder: "Add explanation...",
  modules: {
    toolbar: baseExplanationToolbar,
  },
};

// --- Sorting and Filtering State ---
const filterRanking = ref("");
const filterDepartment = ref("");
const searchQuery = ref("");
const sortKey = ref("");
const sortOrder = ref("asc");
const departments = ref([]);
const isCompactView = ref(true); // Toggle for compact/expanded view


// Bulk selection state
const bulkSelectMode = ref(false);
/** Card ids currently persisting demo flag (disables checkbox). */
const savingDemoIds = ref([]);
const selectedCardIds = ref(new Set());
const selectAll = ref(false);

const getDepartmentName = (deptId) => {
  const dept = departments.value.find(d => d.id === deptId);
  return dept ? dept.name : 'Unknown';
};

// Function to get original card number from the full card list
const getOriginalCardNumber = (cardId) => {
  const originalIndex = gameStore.allCards.findIndex(card => card.id === cardId);
  return originalIndex !== -1 ? originalIndex + 1 : '?';
};


// Computed property to filter and sort cards
const sortedFilteredCards = computed(() => {
  let cards = gameStore.allCards;

  // Filter by search query (only if 3 or more characters)
  if (searchQuery.value && searchQuery.value.length >= 3) {
    const query = searchQuery.value.toLowerCase();
    cards = cards.filter((card) => {
      return (
        card.title.toLowerCase().includes(query) ||
        card.content.toLowerCase().includes(query) ||
        card.explanation.toLowerCase().includes(query) ||
        getDepartmentName(card.department).toLowerCase().includes(query)
      );
    });
  }

  // Filter by ranking
  if (filterRanking.value) {
    cards = cards.filter(
      (card) => String(card.ranking) === filterRanking.value
    );
  }

  // Filter by department
  if (filterDepartment.value) {
    cards = cards.filter(
      (card) => card.department === filterDepartment.value
    );
  }

  // Sort cards if a sort key is selected
  if (sortKey.value) {
    cards = [...cards].sort((a, b) => {
      const compA = a[sortKey.value];
      const compB = b[sortKey.value];

      if (compA < compB) return sortOrder.value === "asc" ? -1 : 1;
      if (compA > compB) return sortOrder.value === "asc" ? 1 : -1;
      return 0;
    });
  }
  return cards;
});

// Function to set the sorting key and toggle sort order
const setSort = (key) => {
  if (sortKey.value === key) {
    // Toggle sort order if the same key is clicked
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
  } else {
    sortKey.value = key;
    sortOrder.value = "asc";
  }
};

// Sanitize HTML to prevent XSS
const sanitizeHTML = (html) => DOMPurify.sanitize(html);
const renderExplanationHtml = (html) => {
  const linked = linkifyHtml(html || "", {
    defaultProtocol: "https",
    target: "_blank",
    rel: "noopener noreferrer",
    ignoreTags: ["a", "script", "style"],
  });

  return DOMPurify.sanitize(linked, {
    ADD_ATTR: ["target", "rel"],
  });
};

// ---------- Remote Data Sync Setup ----------
// Replace these values with your original Supabase project's details.
//const originalSupabaseUrl = 'https://giohqrjvxnmphrifufhd.supabase.co/rest/v1/cards';
const REMOTE_API_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imdpb2hxcmp2eG5tcGhyaWZ1ZmhkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDE5NDE3ODAsImV4cCI6MjA1NzUxNzc4MH0.izGCPVaP-CFLF-2HTyXk1d7sBRPlblvaRUdYONpRMrc'
// Fetch remote card data via the REST API
const fetchRemoteCardData = async () => {
  try {
    const response = await axios.get(
      'https://giohqrjvxnmphrifufhd.supabase.co/rest/v1/cards',
      {
        headers: {
          'apikey': REMOTE_API_KEY,
          'Authorization': `Bearer ${REMOTE_API_KEY}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation' // Add this header
        },
        params: {
          select: '*'
        }
      }
    );
    return response.data;
    
  } catch (error) {
    console.error("Error details:", error.response?.data || error.message);
    return [];
  }

};
// Sync remote card data with the local gameStore cards
const syncCards = async () => {
  console.log("Sync clicked");
  const remoteCards = await fetchRemoteCardData();
  console.log("Fetched remote cards:", remoteCards);
  // Simple check: compare JSON string representations.
        if (remoteCards && JSON.stringify(remoteCards) !== JSON.stringify(gameStore.allCards)) {
        gameStore.allCards = remoteCards;
        console.log("Remote card data synchronized:", gameStore.allCards);
    ElMessage.success("Cards synchronized with remote data.");
    await syncRemoteToLocal();
  } else {
    console.log("No changes in remote card data.");
    ElMessage.info("No changes in card data.");
  }
};

const syncRemoteToLocal = async () => {
  // Fetch the remote card data
  const remoteCards = await fetchRemoteCardData();
  if (remoteCards.length === 0) {
    console.log("No remote cards found.");
    return;
  }

  // Use upsert to update existing rows and insert new ones.
  // Make sure that the remoteCards data structure matches your local table schema.
  const { data, error } = await supabase
    .from('cards')
    .upsert(remoteCards, { onConflict: 'id' }); // 'id' is assumed to be the primary key
  
  if (error) {
    console.error("Error saving cards to target database:", error);
  } else {
    console.log("Cards successfully saved to target database:", data);
  }
};

// Optionally, set up periodic syncing (e.g., every hour)
let syncInterval;


onMounted(() => {
  // Initialize admin authentication
  checkAuth();
  
  // Fetch departments
  fetchDepartments();

  // Listen for logout event from nav
  window.addEventListener('logout-requested', logout);

  // Set up periodic remote sync (every hour)
  // syncInterval = setInterval(() => {
  //   if (user.value) {
  //     syncCards();
  //   }
  // }, 3600000);
});

onUnmounted(() => {
  if (syncInterval) clearInterval(syncInterval);
  // Remove logout event listener
  window.removeEventListener('logout-requested', logout);
});

// ---------- Local Admin Authentication Setup ----------

const user = ref(null);
const email = ref("");
const password = ref("");
const loading = ref(false);
const errorMessage = ref("");

// Forgot-password flow
const showForgotPw = ref(false);
const resetEmail = ref("");
const resetLoading = ref(false);
const resetMessage = ref("");
const resetIsError = ref(false);

const sendResetEmail = async () => {
  resetMessage.value = "";
  resetIsError.value = false;
  if (!resetEmail.value.trim()) {
    resetMessage.value = "Please enter your admin email address.";
    resetIsError.value = true;
    return;
  }
  resetLoading.value = true;
  try {
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(
      resetEmail.value.trim(),
      { redirectTo }
    );
    if (error) throw error;
    resetMessage.value = "Check your inbox — a reset link has been sent.";
  } catch (err) {
    resetMessage.value = err.message || "Could not send reset email.";
    resetIsError.value = true;
  } finally {
    resetLoading.value = false;
  }
};

const cancelForgotPw = () => {
  showForgotPw.value = false;
  resetEmail.value = "";
  resetMessage.value = "";
  resetIsError.value = false;
};


// Check authentication from localStorage
const checkAuth = async () => {
  const adminUser = localStorage.getItem('adminUser');
  const loggedIn = localStorage.getItem('loggedIn');
  
  if (adminUser && loggedIn === 'true') {
    try {
      user.value = JSON.parse(adminUser);
      await gameStore.fetchAllCards();
    } catch (error) {
      console.error("Error parsing admin user:", error);
      localStorage.removeItem('adminUser');
      localStorage.removeItem('loggedIn');
      user.value = null;
    }
  } else {
    user.value = null;
  }
};

// Fetch departments from database
const fetchDepartments = async () => {
  const { data, error } = await supabase.from("departments").select("*");
  if (error) {
    console.error("Error fetching departments:", error);
  } else {
    departments.value = data;
  }
};

// ---------- Local Admin Login Function ----------
const login = async () => {
  loading.value = true;
  errorMessage.value = "";

  if (!email.value.trim() || !password.value) {
    errorMessage.value = "Email and password are required.";
    loading.value = false;
    return;
  }

  try {
    // Step 1: verify credentials via Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: email.value.trim(),
      password: password.value,
    });

    if (authError) {
      throw new Error('Invalid email or password.');
    }

    // Step 2: confirm the authenticated user has an admin_users record
    const { data: adminUser, error: adminErr } = await supabase
      .from('admin_users')
      .select('*')
      .eq('email', authData.user.email)
      .maybeSingle();

    // Sign out the Supabase Auth session — admin state is kept in localStorage
    await supabase.auth.signOut();

    if (adminErr || !adminUser) {
      throw new Error('This account does not have admin access.');
    }

    // Step 3: persist admin session in localStorage (existing pattern)
    localStorage.setItem('adminEmail', adminUser.email);
    localStorage.setItem('adminUser', JSON.stringify({
      id: adminUser.id,
      email: adminUser.email,
      role: adminUser.role
    }));
    localStorage.setItem('loggedIn', 'true');

    user.value = {
      id: adminUser.id,
      email: adminUser.email,
      role: adminUser.role
    };

    await gameStore.fetchAllCards();
    ElMessage.success("Login successful!");
  } catch (err) {
    errorMessage.value = err.message;
    ElMessage.error(err.message);
  } finally {
    loading.value = false;
  }
};

// ---------- Local Admin Logout Function ----------
const logout = async () => {
  console.log("Logout function called in CardManagement");
  try {
    user.value = null;
    localStorage.removeItem("loggedIn");
    localStorage.removeItem("adminUser");
    localStorage.removeItem("adminEmail");
    localStorage.removeItem("userDeptAccess");
    gameStore.allCards = [];
    ElMessage.success("Logged out successfully.");
    
    // Redirect to home page after successful admin logout
    console.log("Redirecting to home page");
    window.location.href = '/';
  } catch (err) {
    console.error("Error during logout:", err);
  }
};

// ---------- Card Management Functions ----------
const openAddModal = () => {
  editingCard.value = { 
    title: "", 
    content: "", 
    explanation: "", 
    ranking: lastUsedRanking.value,
    department: lastUsedDepartment.value,
    useForDemo: false,
  };
  isEditing.value = false;
  showModal.value = true;
};

const editCard = (card) => {
  editingCard.value = { ...card };
  isEditing.value = true;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  isCardExplanationHtmlMode.value = false;
};

const openExplanationModal = (card) => {
  selectedCard.value = card;
  isEditingExplanation.value = false; // Start in view mode
  editingExplanation.value = card?.explanation || "";
  originalExplanation.value = card?.explanation || "";
  showExplanationModal.value = true;
};

const closeExplanationModal = () => {
  showExplanationModal.value = false;
  selectedCard.value = null;
  isEditingExplanation.value = false;
  editingExplanation.value = "";
  originalExplanation.value = "";
  isModalExplanationHtmlMode.value = false;
};

const startEditExplanation = () => {
  // Use nextTick to ensure DOM is ready
  setTimeout(() => {
    isEditingExplanation.value = true;
    editingExplanation.value = selectedCard.value?.explanation || "";
    originalExplanation.value = selectedCard.value?.explanation || "";
  }, 100);
};

const saveExplanation = async () => {
  try {
    if (selectedCard.value) {
      const explanationContent = editingExplanation.value;
      
      const updatedCard = {
        id: selectedCard.value.id,
        title: selectedCard.value.title,
        content: selectedCard.value.content,
        explanation: explanationContent,
        ranking: selectedCard.value.ranking,
        department: selectedCard.value.department
      };
      
      console.log('Saving explanation:', explanationContent);
      console.log('Updated card data:', updatedCard);
      await gameStore.updateCard(updatedCard);
      selectedCard.value = updatedCard;
      originalExplanation.value = explanationContent;
      isEditingExplanation.value = false;
      
      ElMessage.success("Explanation updated successfully!");
    }
  } catch (error) {
    console.error("Error saving explanation:", error);
    ElMessage.error("Error saving explanation: " + error.message);
  }
};


const truncateExplanation = (explanation) => {
  if (!explanation) return '';
  
  // Remove HTML tags for length calculation
  const textContent = explanation.replace(/<[^>]*>/g, '');
  
  if (textContent.length <= 200) {
    return explanation;
  }
  
  // Try to preserve some basic HTML structure
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = explanation;
  
  // Get the first few elements or text nodes
  let result = '';
  let charCount = 0;
  
  const walkTextNodes = (node) => {
    if (charCount >= 200) return;
    
    if (node.nodeType === Node.TEXT_NODE) {
      const remainingChars = 200 - charCount;
      const text = node.textContent;
      
      if (text.length <= remainingChars) {
        result += text;
        charCount += text.length;
      } else {
        result += text.substring(0, remainingChars) + '...';
        charCount = 200;
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      
      // Preserve basic formatting tags
      if (['p', 'div', 'span', 'strong', 'b', 'em', 'i', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
        result += `<${tagName}>`;
        
        for (const child of node.childNodes) {
          if (charCount >= 200) break;
          walkTextNodes(child);
        }
        
        if (charCount < 200) {
          result += `</${tagName}>`;
        }
      } else {
        // For other tags, just process their text content
        for (const child of node.childNodes) {
          if (charCount >= 200) break;
          walkTextNodes(child);
        }
      }
    }
  };
  
  for (const child of tempDiv.childNodes) {
    if (charCount >= 200) break;
    walkTextNodes(child);
  }
  
  return result;
};

const addCard = async () => {
  try {
    // Validate required fields
    if (!editingCard.value.department) {
      alert("Please select a department");
      return;
    }
    if (!editingCard.value.ranking || editingCard.value.ranking < 1 || editingCard.value.ranking > 4) {
      alert("Please select a ranking between 1 and 4");
      return;
    }
    
    await gameStore.addCard(editingCard.value);
    
    // Remember the department and ranking for next new card
    lastUsedDepartment.value = editingCard.value.department || "";
    lastUsedRanking.value = editingCard.value.ranking || 1;
    
    await gameStore.fetchAllCards();
    closeModal();
  } catch (error) {
    console.error("Error adding card:", error);
  }
};

const updateCard = async () => {
  try {
    // Validate required fields
    if (!editingCard.value.department) {
      alert("Please select a department");
      return;
    }
    if (!editingCard.value.ranking || editingCard.value.ranking < 1 || editingCard.value.ranking > 4) {
      alert("Please select a ranking between 1 and 4");
      return;
    }
    
    console.log("Updating card with:", editingCard.value);
    await gameStore.updateCard(editingCard.value);
    
    // Remember the department and ranking for next new card
    lastUsedDepartment.value = editingCard.value.department || "";
    lastUsedRanking.value = editingCard.value.ranking || 1;
    
    await gameStore.fetchAllCards();
    console.log("Card updated, refreshed cards:", gameStore.allCards);
    ElMessage.success("Card updated successfully!");
    closeModal();
  } catch (error) {
    console.error("Error updating card:", error);
    ElMessage.error("Error updating card: " + error.message);
  }
};

const confirmDeleteCard = async () => {
  if (confirm(`Are you sure you want to delete "${editingCard.value.title}"?`)) {
    try {
      await gameStore.deleteCard(editingCard.value.id);
      await gameStore.fetchAllCards();
      ElMessage.success("Card deleted successfully!");
      closeModal();
    } catch (error) {
      console.error("Error deleting card:", error);
      ElMessage.error("Error deleting card: " + error.message);
    }
  }
};

const toggleCardUseForDemo = async (card, event) => {
  event.stopPropagation();
  const useForDemo = event.target.checked;
  if (savingDemoIds.value.includes(card.id)) {
    event.target.checked = !useForDemo;
    return;
  }
  savingDemoIds.value = [...savingDemoIds.value, card.id];
  try {
    await gameStore.updateCard({
      id: card.id,
      title: card.title,
      content: card.content,
      explanation: card.explanation,
      ranking: card.ranking,
      department: card.department,
      useForDemo,
    });
  } catch (error) {
    event.target.checked = !useForDemo;
    console.error("Error updating demo flag:", error);
    ElMessage.error(error.message || "Could not update demo flag");
  } finally {
    savingDemoIds.value = savingDemoIds.value.filter((id) => id !== card.id);
  }
};

// Bulk selection methods
const toggleBulkSelectMode = () => {
  bulkSelectMode.value = !bulkSelectMode.value;
  if (!bulkSelectMode.value) {
    selectedCardIds.value.clear();
    selectAll.value = false;
  }
};

const toggleSelectAll = () => {
  selectAll.value = !selectAll.value;
  selectedCardIds.value.clear();
  
  if (selectAll.value) {
    sortedFilteredCards.value.forEach(card => {
      selectedCardIds.value.add(card.id);
    });
  }
};

const toggleCardSelection = (cardId, event) => {
  event.stopPropagation(); // Prevent row click from triggering
  
  if (selectedCardIds.value.has(cardId)) {
    selectedCardIds.value.delete(cardId);
  } else {
    selectedCardIds.value.add(cardId);
  }
  
  // Update selectAll state
  selectAll.value = selectedCardIds.value.size === sortedFilteredCards.value.length;
};

const bulkDeleteCards = async () => {
  if (selectedCardIds.value.size === 0) {
    ElMessage.warning("No cards selected for deletion");
    return;
  }
  
  const confirmMessage = `Are you sure you want to delete ${selectedCardIds.value.size} selected card(s)?`;
  if (confirm(confirmMessage)) {
    try {
      for (const cardId of selectedCardIds.value) {
        await gameStore.deleteCard(cardId);
      }
      await gameStore.fetchAllCards();
      selectedCardIds.value.clear();
      selectAll.value = false;
      ElMessage.success(`Successfully deleted ${selectedCardIds.value.size} cards`);
    } catch (error) {
      console.error("Error deleting cards:", error);
      ElMessage.error("Error deleting cards: " + error.message);
    }
  }
};

// ---------- Single card delete from table ---------------
const confirmDeleteSingleCard = async (card) => {
  if (confirm(`Delete "${card.title}"?`)) {
    try {
      await gameStore.deleteCard(card.id);
      await gameStore.fetchAllCards();
      ElMessage.success("Card deleted.");
    } catch (error) {
      console.error("Error deleting card:", error);
      ElMessage.error("Error deleting card: " + error.message);
    }
  }
};


</script>

<style src="../styles/_cardManagement.scss" lang="scss"></style>
